/**
 * Edit entry page - update meaning, spellings, and etymologies in one consolidated form.
 */
import { LitElement, html, css, unsafeCSS } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import { gql } from '@apollo/client'
import { apolloClient } from '../utils/apollo.js'
import { t, getCurrentLanguageId } from '../utils/i18n.js'
import styles from './edit-entry-page.css?inline'
import '../components/etymology-root-display.js'
import '../components/etymology-display.js'
import '../components/meaning-grammeme-display.js'
import '../components/meaning-grammeme-form.js'
import '@material/web/button/filled-button.js'
import '@material/web/button/outlined-button.js'
import '@material/web/textfield/filled-text-field.js'
import '@material/web/select/filled-select.js'
import '@material/web/select/select-option.js'

const UPDATE_MEANING_MUTATION = gql`
  mutation UpdateMeaning($input: UpdateMeaningInput!) {
    updateMeaning(input: $input) {
      id
    }
  }
`

const UPDATE_SPELLING_MUTATION = gql`
  mutation UpdateSpelling($input: UpdateSpellingInput!) {
    updateSpelling(input: $input) {
      id
    }
  }
`

const CREATE_SPELLING_MUTATION = gql`
  mutation CreateSpelling($spelling: String!, $systemId: ID!, $meaningId: ID!) {
    createSpelling(input: { spelling: $spelling, systemId: $systemId, meaningId: $meaningId }) {
      id
    }
  }
`

const DELETE_SPELLING_MUTATION = gql`
  mutation DeleteSpelling($id: ID!) {
    deleteSpelling(id: $id)
  }
`

const UPDATE_ETYMOLOGY_MUTATION = gql`
  mutation UpdateEtymology($input: UpdateEtymologyInput!) {
    updateEtymology(input: $input) {
      id
    }
  }
`

const CREATE_ETYMOLOGY_MUTATION = gql`
  mutation CreateEtymology($meaningId: ID!, $title: String, $description: String) {
    createEtymology(input: { meaningId: $meaningId, title: $title, description: $description }) {
      id
    }
  }
`

const DELETE_ETYMOLOGY_MUTATION = gql`
  mutation DeleteEtymology($id: ID!) {
    deleteEtymology(id: $id)
  }
`

const CREATE_ETYMOLOGY_ROOT_MUTATION = gql`
  mutation CreateEtymologyRoot($etymologyId: ID!, $rootId: ID!) {
    createEtymologyRoot(input: { etymologyId: $etymologyId, rootId: $rootId }) {
      id
    }
  }
`

const DELETE_ETYMOLOGY_ROOT_MUTATION = gql`
  mutation DeleteEtymologyRoot($id: ID!) {
    deleteEtymologyRoot(id: $id)
  }
`

const GET_LANGUAGES_QUERY = gql`
  query GetLanguages {
    languages { id name }
  }
`

const GET_MEANING_TYPES_QUERY = gql`
  query GetMeaningTypes {
    meaningTypes { id name }
  }
`

const GET_SPELLING_SYSTEMS_QUERY = gql`
  query GetSpellingSystems {
    spellingSystems { id name }
  }
`

const MEANING_BY_ID_QUERY = gql`
  query MeaningById($id: ID!) {
    meaning(id: $id) {
      id
      type { id name }
      word { id }
      language { id name }
    }
  }
`

const SEARCH_MEANINGS_QUERY = gql`
  query SearchMeanings($filter: String, $languageId: ID, $limit: Int, $offset: Int, $displayLanguageId: ID, $filterMode: SearchMeaningsFilterMode) {
    searchMeanings(filter: $filter, languageId: $languageId, limit: $limit, offset: $offset, displayLanguageId: $displayLanguageId, filterMode: $filterMode) {
      meaning {
        id
        type { id name }
        word { id }
        language { id name }
      }
      primarySpelling
      displaySpelling
    }
  }
`

const SPELLINGS_BY_MEANING_QUERY = gql`
  query SpellingsByMeaningId($meaningId: ID!) {
    spellingsByMeaningId(meaningId: $meaningId) {
      id
      spelling
      system { id name }
      meaning { id }
    }
  }
`

const GET_ETYMOLOGIES_BY_MEANING_QUERY = gql`
  query GetEtymologiesByMeaningId($meaningId: ID!, $displayLanguageId: ID) {
    etymologiesByMeaningId(meaningId: $meaningId, displayLanguageId: $displayLanguageId) {
      id
      title
      description
      meaning { id }
      roots {
        id
        rootSpelling
        displaySpelling
        root {
          id
          type { name }
          language { name }
        }
      }
    }
  }
`

const MEANING_GRAMMEMES_QUERY = gql`
  query MeaningGrammemes($meaningId: ID!) {
    meaningGrammemes(meaningId: $meaningId) {
      id
      meaning { id }
      grammeme { id value feature { id name } }
    }
  }
`

const DELETE_MEANING_GRAMMEME_MUTATION = gql`
  mutation DeleteMeaningGrammeme($id: ID!) {
    deleteMeaningGrammeme(id: $id)
  }
`

const PAGE_SIZE = 10

interface SpellingRow {
  id: string
  spelling: string
  systemId: string
  systemName?: string
  isNew?: boolean
}

interface EtymologyRootRow {
  id: string
  rootId: string
  typeName: string
  languageName: string
  displaySpelling?: string  // from rootSpelling in API
}

interface EtymologyRow {
  id: string
  title: string
  description: string
  meaningId: string
  roots: EtymologyRootRow[]
  isNew?: boolean
}

@customElement('edit-entry-page')
export class EditEntryPage extends LitElement {
  /** When set (e.g. from URL ?meaningId=), preselect this meaning for editing. */
  @property({ type: String }) initialMeaningId = ''

  @state() private languages: Array<{ id: string; name: string }> = []
  @state() private meaningTypes: Array<{ id: string; name: string }> = []
  @state() private spellingSystems: Array<{ id: string; name: string }> = []
  @state() private loading = false
  @state() private error = ''
  @state() private success = ''

  // Meaning search
  @state() private meaningSearchLanguageId = ''
  @state() private meaningSearchFilter = ''
  @state() private meaningSearchResults: Array<{ meaning: { id: string; type: { id: string; name: string }; word: { id: string }; language: { id: string; name: string } }; primarySpelling: string | null; displaySpelling: string | null }> = []
  @state() private meaningSearchLoading = false
  @state() private meaningSearchPage = 0
  @state() private meaningSearchHasMore = false
  @state() private selectedMeaning: { id: string; typeId: string; wordId: string; languageId: string; typeName: string; languageName: string; displaySpelling: string } | null = null

  // Loaded data for selected meaning
  @state() private spellings: SpellingRow[] = []
  @state() private etymologies: EtymologyRow[] = []
  @state() private meaningGrammemes: Array<{ id: string; grammeme: { id: string; value: string; feature: { id: string; name: string } } }> = []
  @state() private meaningGrammemesRefreshTrigger = 0
  @state() private deletingGrammemeId = ''
  @state() private loadingSpellings = false
  @state() private loadingEtymologies = false

  // Per-section updating
  @state() private updatingMeaning = false
  @state() private updatingSpellingId = ''
  @state() private creatingSpelling = false
  @state() private updatingEtymologyId = ''
  @state() private creatingEtymology = false

  // Etymology root search (for adding roots)
  @state() private etymologyRootSearchForEtymologyId = ''
  @state() private etymologyRootSearchLanguageId = ''
  @state() private etymologyRootSearchFilter = ''
  @state() private etymologyRootSearchMode: 'includes' | 'equals' = 'includes'
  @state() private etymologyRootSearchResults: Array<{ meaning: { id: string; type: { id: string; name: string }; word: { id: string }; language: { id: string; name: string } }; primarySpelling: string | null; displaySpelling: string | null }> = []
  @state() private etymologyRootSearchLoading = false
  @state() private deletingEtymologyRootId = ''

  private _meaningSearchDebounce: ReturnType<typeof setTimeout> | null = null
  private _etymologyRootSearchDebounce: ReturnType<typeof setTimeout> | null = null
  private _successClearTimeout: ReturnType<typeof setTimeout> | null = null
  private _initialMeaningIdHandled = false

  /** Set success message and clear it after 3s so E2E and users can see it reliably. */
  private setSuccessMessage() {
    if (this._successClearTimeout) clearTimeout(this._successClearTimeout)
    this.success = t('editDialog.success')
    this._successClearTimeout = setTimeout(() => {
      this.success = ''
      this._successClearTimeout = null
    }, 3000)
  }

  static styles = css`${unsafeCSS(styles)}`

  async connectedCallback() {
    super.connectedCallback()
    await this.loadData()
    window.addEventListener('i18n-updated', () => {
      this.requestUpdate()
      if (this.selectedMeaning) this.loadSpellingsAndEtymologies()
    })
  }

  disconnectedCallback() {
    if (this._successClearTimeout) clearTimeout(this._successClearTimeout)
    super.disconnectedCallback()
  }

  updated(changedProperties: Map<string, unknown>) {
    super.updated?.(changedProperties)
    if (this.initialMeaningId && !this._initialMeaningIdHandled && this.meaningTypes.length > 0 && this.languages.length > 0) {
      this._initialMeaningIdHandled = true
      this.loadMeaningById(this.initialMeaningId)
    }
  }

  private async loadMeaningById(meaningId: string) {
    try {
      const [meaningRes, spellingsRes] = await Promise.all([
        apolloClient.query({ query: MEANING_BY_ID_QUERY, variables: { id: meaningId } }),
        apolloClient.query({ query: SPELLINGS_BY_MEANING_QUERY, variables: { meaningId } }),
      ])
      const meaning = (meaningRes.data as any).meaning
      if (!meaning) return
      const spellings = (spellingsRes.data as any).spellingsByMeaningId ?? []
      const primarySpelling = spellings[0]?.spelling ?? null
      await this.selectMeaningForEdit({
        meaning: { id: meaning.id, type: meaning.type, word: meaning.word, language: meaning.language },
        primarySpelling,
        displaySpelling: primarySpelling,
      })
    } catch (_) {
      // ignore
    }
  }

  private async loadData() {
    this.loading = true
    this.error = ''
    try {
      const [langRes, typeRes, sysRes] = await Promise.all([
        apolloClient.query({ query: GET_LANGUAGES_QUERY }),
        apolloClient.query({ query: GET_MEANING_TYPES_QUERY }),
        apolloClient.query({ query: GET_SPELLING_SYSTEMS_QUERY }),
      ])
      this.languages = (langRes.data as any).languages ?? []
      this.meaningTypes = (typeRes.data as any).meaningTypes ?? []
      this.spellingSystems = (sysRes.data as any).spellingSystems ?? []
    } catch (err: any) {
      this.error = err.message || t('editDialog.error')
    } finally {
      this.loading = false
    }
  }

  private async searchMeanings(page = 0) {
    if (!this.meaningSearchLanguageId || this.meaningSearchFilter.trim().length === 0) {
      this.meaningSearchResults = []
      this.meaningSearchPage = 0
      this.meaningSearchHasMore = false
      return
    }
    this.meaningSearchLoading = true
    this.meaningSearchPage = page
    try {
      const result = await apolloClient.query({
        query: SEARCH_MEANINGS_QUERY,
        variables: {
          filter: this.meaningSearchFilter.trim() || null,
          languageId: this.meaningSearchLanguageId || null,
          limit: PAGE_SIZE,
          offset: page * PAGE_SIZE,
          displayLanguageId: getCurrentLanguageId() || null,
        },
      })
      const results = (result.data as any).searchMeanings ?? []
      this.meaningSearchResults = results
      this.meaningSearchHasMore = results.length === PAGE_SIZE
    } catch (_) {
      this.meaningSearchResults = []
      this.meaningSearchHasMore = false
    } finally {
      this.meaningSearchLoading = false
    }
  }

  private onMeaningSearchInput() {
    if (this._meaningSearchDebounce) clearTimeout(this._meaningSearchDebounce)
    this._meaningSearchDebounce = setTimeout(() => this.searchMeanings(0), 300)
  }

  private previousMeaningSearchPage() {
    if (this.meaningSearchPage === 0 || this.meaningSearchLoading) return
    this.searchMeanings(this.meaningSearchPage - 1)
  }

  private nextMeaningSearchPage() {
    if (!this.meaningSearchHasMore || this.meaningSearchLoading) return
    this.searchMeanings(this.meaningSearchPage + 1)
  }

  private async selectMeaningForEdit(item: { meaning: { id: string; type: { id: string; name: string }; word: { id: string }; language: { id: string; name: string } }; primarySpelling: string | null; displaySpelling: string | null }) {
    this.selectedMeaning = {
      id: item.meaning.id,
      typeId: item.meaning.type.id,
      wordId: item.meaning.word.id,
      languageId: item.meaning.language.id,
      typeName: item.meaning.type.name ?? '',
      languageName: item.meaning.language.name ?? '',
      displaySpelling: item.displaySpelling ?? item.primarySpelling ?? '',
    }
    this.error = ''
    this.success = ''
    await this.loadSpellingsAndEtymologies()
  }

  private async loadSpellingsAndEtymologies() {
    if (!this.selectedMeaning) return
    this.loadingSpellings = true
    this.loadingEtymologies = true
    try {
      const meaningId = this.selectedMeaning.id
      const displayLanguageId = getCurrentLanguageId() || undefined
      const [spellRes, etymRes, mgRes] = await Promise.all([
        apolloClient.query({ query: SPELLINGS_BY_MEANING_QUERY, variables: { meaningId } }),
        apolloClient.query({ query: GET_ETYMOLOGIES_BY_MEANING_QUERY, variables: { meaningId, displayLanguageId } }),
        apolloClient.query({ 
          query: MEANING_GRAMMEMES_QUERY,
          variables: { meaningId }
        }),
      ])
      const spellingsData = (spellRes.data as any).spellingsByMeaningId ?? []
      this.spellings = spellingsData.map((s: any) => ({
        id: s.id,
        spelling: s.spelling,
        systemId: s.system?.id ?? '',
        systemName: s.system?.name,
        isNew: false,
      }))
      const etymologiesData = (etymRes.data as any).etymologiesByMeaningId ?? []
      this.etymologies = etymologiesData.map((e: any) => ({
        id: e.id,
        title: e.title ?? '',
        description: e.description ?? '',
        meaningId: e.meaning?.id ?? meaningId,
        roots: (e.roots ?? []).map((r: any) => ({
          id: r.id,
          rootId: r.root?.id ?? '',
          typeName: r.root?.type?.name ?? '',
          languageName: r.root?.language?.name ?? '',
          displaySpelling: r.displaySpelling ?? r.rootSpelling ?? undefined,
        })),
        isNew: false,
      }))
      this.meaningGrammemes = (mgRes.data as any).meaningGrammemes ?? []
    } catch (err: any) {
      this.error = err.message || t('editDialog.error')
    } finally {
      this.loadingSpellings = false
      this.loadingEtymologies = false
    }
  }

  private clearSelection() {
    this.selectedMeaning = null
    this.spellings = []
    this.etymologies = []
    this.meaningGrammemes = []
    this.etymologyRootSearchForEtymologyId = ''
    this.etymologyRootSearchResults = []
    this.error = ''
    this.success = ''
  }

  private async refreshMeaningGrammemes() {
    if (!this.selectedMeaning) return
    try {
      const mgRes = await apolloClient.query({
        query: MEANING_GRAMMEMES_QUERY,
        variables: { meaningId: this.selectedMeaning.id },
        fetchPolicy: 'network-only',
      })
      this.meaningGrammemes = (mgRes.data as any).meaningGrammemes ?? []
      this.meaningGrammemesRefreshTrigger++
    } catch {
      // ignore
    }
  }

  private async handleMeaningGrammemeRemove(e: CustomEvent<{ id: string }>) {
    const id = e.detail?.id
    if (!id) return
    this.deletingGrammemeId = id
    try {
      await apolloClient.mutate({ mutation: DELETE_MEANING_GRAMMEME_MUTATION, variables: { id } })
      await this.refreshMeaningGrammemes()
      this.setSuccessMessage()
    } finally {
      this.deletingGrammemeId = ''
    }
  }

  private async handleUpdateMeaning() {
    if (!this.selectedMeaning) return
    this.updatingMeaning = true
    this.error = ''
    this.success = ''
    try {
      await apolloClient.mutate({
        mutation: UPDATE_MEANING_MUTATION,
        variables: {
          input: {
            id: this.selectedMeaning.id,
            typeId: this.selectedMeaning.typeId,
            wordId: this.selectedMeaning.wordId,
            languageId: this.selectedMeaning.languageId,
          },
        },
      })
      const typeName = this.meaningTypes.find(tp => tp.id === this.selectedMeaning!.typeId)?.name ?? ''
      const languageName = this.languages.find(l => l.id === this.selectedMeaning!.languageId)?.name ?? ''
      this.selectedMeaning = { ...this.selectedMeaning!, typeName, languageName }
      this.setSuccessMessage()
    } catch (err: any) {
      this.error = err.message || t('editDialog.error')
    } finally {
      this.updatingMeaning = false
    }
  }

  private updateSpellingRow(id: string, field: 'spelling' | 'systemId', value: string) {
    this.spellings = this.spellings.map(s =>
      s.id === id ? { ...s, [field]: value } : s
    )
  }

  private async handleUpdateSpelling(spelling: SpellingRow) {
    if (spelling.isNew) return
    this.updatingSpellingId = spelling.id
    this.error = ''
    this.success = ''
    try {
      await apolloClient.mutate({
        mutation: UPDATE_SPELLING_MUTATION,
        variables: {
          input: {
            id: spelling.id,
            spelling: spelling.spelling,
            systemId: spelling.systemId,
            meaningId: this.selectedMeaning!.id,
          },
        },
      })
      this.setSuccessMessage()
    } catch (err: any) {
      this.error = err.message || t('editDialog.error')
    } finally {
      this.updatingSpellingId = ''
    }
  }

  private addSpellingRow() {
    this.spellings = [...this.spellings, {
      id: `new-${Date.now()}`,
      spelling: '',
      systemId: this.spellingSystems[0]?.id ?? '',
      isNew: true,
    }]
  }

  private async handleCreateSpelling(spelling: SpellingRow) {
    if (!spelling.isNew || !this.selectedMeaning) return
    this.creatingSpelling = true
    this.error = ''
    this.success = ''
    try {
      const res = await apolloClient.mutate({
        mutation: CREATE_SPELLING_MUTATION,
        variables: {
          spelling: spelling.spelling.trim(),
          systemId: spelling.systemId,
          meaningId: this.selectedMeaning.id,
        },
      })
      const newId = (res.data as any).createSpelling.id
      this.spellings = this.spellings.map(s =>
        s.id === spelling.id ? { ...s, id: newId, isNew: false } : s
      )
      this.setSuccessMessage()
    } catch (err: any) {
      this.error = err.message || t('editDialog.error')
    } finally {
      this.creatingSpelling = false
    }
  }

  private async handleDeleteSpelling(id: string) {
    if (id.startsWith('new-')) {
      this.spellings = this.spellings.filter(s => s.id !== id)
      return
    }
    this.updatingSpellingId = id
    this.error = ''
    this.success = ''
    try {
      await apolloClient.mutate({
        mutation: DELETE_SPELLING_MUTATION,
        variables: { id },
      })
      this.spellings = this.spellings.filter(s => s.id !== id)
      this.setSuccessMessage()
    } catch (err: any) {
      this.error = err.message || t('editDialog.error')
    } finally {
      this.updatingSpellingId = ''
    }
  }

  private updateEtymologyRow(id: string, field: 'title' | 'description', value: string) {
    this.etymologies = this.etymologies.map(e =>
      e.id === id ? { ...e, [field]: value } : e
    )
  }

  private async handleUpdateEtymology(etymology: EtymologyRow) {
    if (etymology.isNew) return
    this.updatingEtymologyId = etymology.id
    this.error = ''
    this.success = ''
    try {
      await apolloClient.mutate({
        mutation: UPDATE_ETYMOLOGY_MUTATION,
        variables: {
          input: {
            id: etymology.id,
            title: etymology.title || null,
            description: etymology.description || null,
            meaningId: etymology.meaningId,
          },
        },
      })
      this.setSuccessMessage()
    } catch (err: any) {
      this.error = err.message || t('editDialog.error')
    } finally {
      this.updatingEtymologyId = ''
    }
  }

  private addEtymologyRow() {
    if (!this.selectedMeaning) return
    this.etymologies = [...this.etymologies, {
      id: `new-${Date.now()}`,
      title: '',
      description: '',
      meaningId: this.selectedMeaning.id,
      roots: [],
      isNew: true,
    }]
  }

  private startAddEtymologyRoot(etymologyId: string) {
    this.etymologyRootSearchForEtymologyId = etymologyId
    this.etymologyRootSearchLanguageId = ''
    this.etymologyRootSearchFilter = ''
    this.etymologyRootSearchMode = 'includes'
    this.etymologyRootSearchResults = []
  }

  private cancelAddEtymologyRoot() {
    this.etymologyRootSearchForEtymologyId = ''
    this.etymologyRootSearchResults = []
  }

  private async searchEtymologyRootMeanings(page = 0) {
    if (!this.etymologyRootSearchLanguageId || this.etymologyRootSearchFilter.trim().length === 0) return
    this.etymologyRootSearchLoading = true
    try {
      const filterMode = this.etymologyRootSearchMode === 'equals' ? 'EQUALS' : 'INCLUDES'
      const result = await apolloClient.query({
        query: SEARCH_MEANINGS_QUERY,
        variables: {
          filter: this.etymologyRootSearchFilter.trim() || null,
          languageId: this.etymologyRootSearchLanguageId || null,
          limit: PAGE_SIZE,
          offset: page * PAGE_SIZE,
          displayLanguageId: getCurrentLanguageId() || null,
          filterMode,
        },
      })
      this.etymologyRootSearchResults = (result.data as any).searchMeanings ?? []
    } catch (_) {
      this.etymologyRootSearchResults = []
    } finally {
      this.etymologyRootSearchLoading = false
    }
  }

  private onEtymologyRootSearchInput() {
    if (this._etymologyRootSearchDebounce) clearTimeout(this._etymologyRootSearchDebounce)
    this._etymologyRootSearchDebounce = setTimeout(() => this.searchEtymologyRootMeanings(0), 300)
  }

  private setEtymologyRootSearchMode(mode: 'includes' | 'equals') {
    if (this.etymologyRootSearchMode === mode) return
    this.etymologyRootSearchMode = mode
    this.searchEtymologyRootMeanings(0)
  }

  private async handleAddEtymologyRoot(etymologyId: string, item: { meaning: { id: string; type: { name: string }; language: { name: string } }; primarySpelling: string | null; displaySpelling: string | null }) {
    if (etymologyId.startsWith('new-')) return
    this.creatingEtymology = true
    this.error = ''
    this.success = ''
    try {
      const res = await apolloClient.mutate({
        mutation: CREATE_ETYMOLOGY_ROOT_MUTATION,
        variables: {
          etymologyId,
          rootId: item.meaning.id,
        },
      })
      const newId = (res.data as any).createEtymologyRoot.id
      this.etymologies = this.etymologies.map(e => {
        if (e.id !== etymologyId) return e
        const newRoot: EtymologyRootRow = {
          id: newId,
          rootId: item.meaning.id,
          typeName: item.meaning.type.name ?? '',
          languageName: item.meaning.language.name ?? '',
          displaySpelling: item.displaySpelling ?? item.primarySpelling ?? undefined,
        }
        return { ...e, roots: [...e.roots, newRoot] }
      })
      this.etymologyRootSearchForEtymologyId = ''
      this.etymologyRootSearchResults = []
      this.setSuccessMessage()
    } catch (err: any) {
      this.error = err.message || t('editDialog.error')
    } finally {
      this.creatingEtymology = false
    }
  }

  private async handleDeleteEtymologyRoot(etymologyId: string, rootId: string) {
    const etymology = this.etymologies.find(e => e.id === etymologyId)
    const targetRoot = etymology?.roots.find(r => r.id === rootId || r.rootId === rootId)
    const deleteId = targetRoot?.id ?? rootId

    this.deletingEtymologyRootId = deleteId
    this.error = ''
    this.success = ''
    try {
      await apolloClient.mutate({
        mutation: DELETE_ETYMOLOGY_ROOT_MUTATION,
        variables: { id: deleteId },
      })
      this.etymologies = this.etymologies.map(e => {
        if (e.id !== etymologyId) return e
        return { ...e, roots: e.roots.filter(r => r.id !== deleteId) }
      })
      this.setSuccessMessage()
    } catch (err: any) {
      this.error = err.message || t('editDialog.error')
    } finally {
      this.deletingEtymologyRootId = ''
    }
  }

  private async handleCreateEtymology(etymology: EtymologyRow) {
    if (!etymology.isNew || !this.selectedMeaning) return
    this.creatingEtymology = true
    this.error = ''
    this.success = ''
    try {
      const res = await apolloClient.mutate({
        mutation: CREATE_ETYMOLOGY_MUTATION,
        variables: {
          meaningId: this.selectedMeaning.id,
          title: etymology.title.trim() || null,
          description: etymology.description.trim() || null,
        },
      })
      const newId = (res.data as any).createEtymology.id
      this.etymologies = this.etymologies.map(e =>
        e.id === etymology.id ? { ...e, id: newId, isNew: false } : e
      )
      this.setSuccessMessage()
    } catch (err: any) {
      this.error = err.message || t('editDialog.error')
    } finally {
      this.creatingEtymology = false
    }
  }

  private async handleDeleteEtymology(id: string) {
    if (id.startsWith('new-')) {
      this.etymologies = this.etymologies.filter(e => e.id !== id)
      return
    }
    this.updatingEtymologyId = id
    this.error = ''
    this.success = ''
    try {
      await apolloClient.mutate({
        mutation: DELETE_ETYMOLOGY_MUTATION,
        variables: { id },
      })
      this.etymologies = this.etymologies.filter(e => e.id !== id)
      this.setSuccessMessage()
    } catch (err: any) {
      this.error = err.message || t('editDialog.error')
    } finally {
      this.updatingEtymologyId = ''
    }
  }

  private handleCancel() {
    window.history.pushState({}, '', '/')
    window.dispatchEvent(new PopStateEvent('popstate'))
  }

  render() {
    return html`
      <div class="page-container">
        <h1>${t('editDialog.title')}</h1>
        ${this.loading ? html`<div class="loading">${t('common.loading')}</div>` : html`
          ${this.error ? html`<div class="error">${this.error}</div>` : ''}
          ${this.success ? html`<div class="success">${this.success}</div>` : ''}

          ${!this.selectedMeaning ? html`
            <div class="form-content etymology-search">
              <p class="block-hint">${t('editDialog.meaningEditHint')}</p>
              <md-filled-select label=${t('createDialog.language')} .value=${this.meaningSearchLanguageId}
                data-testid="edit-meaning-search-language"
                @change=${(e: Event) => { this.meaningSearchLanguageId = (e.target as any).value; this.searchMeanings(0); }}>
                <md-select-option value=""><div slot="headline">—</div></md-select-option>
                ${this.languages.map(l => html`<md-select-option value=${l.id}><div slot="headline">${t(l.name)}</div></md-select-option>`)}
              </md-filled-select>
              <md-filled-text-field label=${t('createDialog.selectMeaning')} .value=${this.meaningSearchFilter}
                placeholder=${t('createDialog.etymologySearchPlaceholder')}
                data-testid="edit-meaning-search-filter"
                @input=${(e: Event) => { this.meaningSearchFilter = (e.target as HTMLInputElement).value; this.onMeaningSearchInput(); }}>
              </md-filled-text-field>
              ${this.meaningSearchLoading ? html`<div class="loading">${t('common.loading')}</div>` : ''}
              ${!this.meaningSearchLoading && this.meaningSearchResults.length > 0 ? html`
                <ul class="etymology-results">
                  ${this.meaningSearchResults.map(item => html`
                    <li><button type="button" class="etymology-result-btn" data-testid="edit-meaning-result" @click=${() => this.selectMeaningForEdit(item)}>
                      ${item.primarySpelling ?? ''} · ${t(item.meaning.type.name)} · ${t(item.meaning.language.name)} (${item.meaning.id})${item.displaySpelling ? ` — ${item.displaySpelling}` : ''}
                    </button></li>
                  `)}
                </ul>
                <div class="search-pagination">
                  <md-outlined-button type="button" ?disabled=${this.meaningSearchPage === 0 || this.meaningSearchLoading}
                    @click=${this.previousMeaningSearchPage}>
                    ${t('editDialog.paginationPrevious')}
                  </md-outlined-button>
                  <md-outlined-button type="button" ?disabled=${!this.meaningSearchHasMore || this.meaningSearchLoading}
                    @click=${this.nextMeaningSearchPage}>
                    ${t('editDialog.paginationNext')}
                  </md-outlined-button>
                </div>
              ` : ''}
            </div>
          ` : html`
            <div class="form-content">
              <etymology-root-display
                .root=${{
                  id: this.selectedMeaning.id,
                  typeName: this.selectedMeaning.typeName ?? '',
                  languageName: this.selectedMeaning.languageName ?? '',
                  displaySpelling: this.selectedMeaning.displaySpelling ?? '',
                }}
                removable
                @remove=${this.clearSelection}
              ></etymology-root-display>

              <section class="edit-section">
                <h2 class="section-title">${t('editDialog.editMeaning')}</h2>
                <md-filled-select label=${t('createDialog.meaningType')} .value=${this.selectedMeaning.typeId}
                  data-testid="edit-meaning-type"
                  @change=${(e: Event) => {
                    const typeId = (e.target as any).value;
                    const typeName = this.meaningTypes.find(tp => tp.id === typeId)?.name ?? '';
                    this.selectedMeaning = { ...this.selectedMeaning!, typeId, typeName };
                  }}>
                  ${this.meaningTypes.map(tp => html`<md-select-option value=${tp.id}><div slot="headline">${t(tp.name)}</div></md-select-option>`)}
                </md-filled-select>
                <md-filled-select label=${t('createDialog.language')} .value=${this.selectedMeaning.languageId}
                  data-testid="edit-meaning-language"
                  @change=${(e: Event) => {
                    const languageId = (e.target as any).value;
                    const languageName = this.languages.find(l => l.id === languageId)?.name ?? '';
                    this.selectedMeaning = { ...this.selectedMeaning!, languageId, languageName };
                  }}>
                  ${this.languages.map(l => html`<md-select-option value=${l.id}><div slot="headline">${t(l.name)}</div></md-select-option>`)}
                </md-filled-select>
                <md-filled-button data-testid="edit-meaning-update" ?disabled=${this.updatingMeaning} @click=${this.handleUpdateMeaning}>
                  ${this.updatingMeaning ? t('editDialog.updating') : t('editDialog.update')}
                </md-filled-button>
              </section>

              <section class="edit-section">
                <h2 class="section-title">${t('editDialog.editMeaningAndSpellings')}</h2>
                ${this.loadingSpellings ? html`<div class="loading">${t('common.loading')}</div>` : html`
                  ${this.spellings.map(spelling => html`
                    <div class="spelling-row">
                      <md-filled-select label=${t('createDialog.spellingSystem')} .value=${spelling.systemId}
                        @change=${(e: Event) => this.updateSpellingRow(spelling.id, 'systemId', (e.target as any).value)}>
                        ${this.spellingSystems.map(s => html`<md-select-option value=${s.id}><div slot="headline">${t(s.name)}</div></md-select-option>`)}
                      </md-filled-select>
                      <md-filled-text-field label=${t('createDialog.spelling')} .value=${spelling.spelling}
                        @input=${(e: Event) => this.updateSpellingRow(spelling.id, 'spelling', (e.target as HTMLInputElement).value)}
                        placeholder=${t('createDialog.spellingPlaceholder')}>
                      </md-filled-text-field>
                      ${spelling.isNew
                        ? html`
                            <md-filled-button ?disabled=${this.creatingSpelling || !spelling.spelling.trim()} @click=${() => this.handleCreateSpelling(spelling)}>
                              ${t('editDialog.save')}
                            </md-filled-button>
                          `
                        : html`
                            <md-filled-button data-testid="edit-spelling-update" ?disabled=${this.updatingSpellingId === spelling.id} @click=${() => this.handleUpdateSpelling(spelling)}>
                              ${this.updatingSpellingId === spelling.id ? t('editDialog.updating') : t('editDialog.update')}
                            </md-filled-button>
                          `}
                      <md-outlined-button type="button" class="row-remove" @click=${() => this.handleDeleteSpelling(spelling.id)}>${t('common.delete')}</md-outlined-button>
                    </div>
                  `)}
                  <md-outlined-button type="button" @click=${this.addSpellingRow}>${t('createDialog.addSpelling')}</md-outlined-button>
                `}
              </section>

              <section class="edit-section">
                <h2 class="section-title">${t('meaningGrammemes.sectionTitle')}</h2>
                <meaning-grammeme-display
                  .items=${this.meaningGrammemes}
                  .meaningId=${this.selectedMeaning.id}
                  .refreshTrigger=${this.meaningGrammemesRefreshTrigger}
                  .deletingId=${this.deletingGrammemeId}
                  editable
                  @meaning-grammeme-updated=${this.refreshMeaningGrammemes}
                  @meaning-grammeme-remove=${this.handleMeaningGrammemeRemove}
                ></meaning-grammeme-display>
                <meaning-grammeme-form
                  .meaningId=${this.selectedMeaning.id}
                  .languageId=${this.selectedMeaning.languageId}
                  .existingGrammemeIds=${this.meaningGrammemes.map((mg) => mg.grammeme.id)}
                  .existingFeatureIds=${this.meaningGrammemes.map((mg) => mg.grammeme?.feature?.id).filter(Boolean)}
                  @meaning-grammeme-created=${this.refreshMeaningGrammemes}
                ></meaning-grammeme-form>
              </section>

              <section class="edit-section">
                <h2 class="section-title">${t('editDialog.editEtymology')}</h2>
                ${this.loadingEtymologies ? html`<div class="loading">${t('common.loading')}</div>` : html`
                  ${this.etymologies.map(etymology => html`
                    <div class="etymology-edit-block">
                      <div class="etymology-edit-row">
                        <md-filled-text-field label=${t('editDialog.etymologyTitle')} .value=${etymology.title}
                          data-testid=${this.etymologies[0]?.id === etymology.id ? 'edit-etymology-title' : undefined}
                          @input=${(e: Event) => this.updateEtymologyRow(etymology.id, 'title', (e.target as HTMLInputElement).value)}>
                        </md-filled-text-field>
                        <md-filled-text-field label=${t('editDialog.etymologyDescription')} .value=${etymology.description}
                          data-testid=${this.etymologies[0]?.id === etymology.id ? 'edit-etymology-description' : undefined}
                          @input=${(e: Event) => this.updateEtymologyRow(etymology.id, 'description', (e.target as HTMLInputElement).value)}>
                        </md-filled-text-field>
                        ${etymology.isNew
                          ? html`
                              <md-filled-button ?disabled=${this.creatingEtymology} @click=${() => this.handleCreateEtymology(etymology)}>
                                ${t('editDialog.save')}
                              </md-filled-button>
                            `
                          : html`
                              <md-filled-button data-testid="edit-etymology-update" ?disabled=${this.updatingEtymologyId === etymology.id} @click=${() => this.handleUpdateEtymology(etymology)}>
                                ${this.updatingEtymologyId === etymology.id ? t('editDialog.updating') : t('editDialog.update')}
                              </md-filled-button>
                            `}
                        <md-outlined-button type="button" class="row-remove" @click=${() => this.handleDeleteEtymology(etymology.id)}>${t('common.delete')}</md-outlined-button>
                      </div>
                      ${!etymology.isNew ? html`
                        <div class="etymology-roots-block">
                          <etymology-display
                            .etymology=${{
                              id: etymology.id,
                              title: '',
                              description: '',
                              roots: etymology.roots.map(root => ({
                                id: root.rootId,
                                rowId: root.id,
                                typeName: root.typeName ?? '',
                                languageName: root.languageName ?? '',
                                displaySpelling: root.displaySpelling ?? '',
                              })),
                            }}
                            rootsOnly
                            rootsRemovable
                            deletingRootId=${this.deletingEtymologyRootId}
                            @root-remove=${(e: CustomEvent) => {
                              const { etymology, rootIdForDelete } = e.detail
                              this.handleDeleteEtymologyRoot(etymology.id, rootIdForDelete)
                            }}
                          ></etymology-display>
                          ${this.etymologyRootSearchForEtymologyId === etymology.id ? html`
                            <div class="etymology-search">
                              <md-filled-select label=${t('createDialog.language')} .value=${this.etymologyRootSearchLanguageId}
                                @change=${(e: Event) => { this.etymologyRootSearchLanguageId = (e.target as any).value; this.searchEtymologyRootMeanings(0); }}>
                                <md-select-option value=""><div slot="headline">—</div></md-select-option>
                                ${this.languages.map(l => html`<md-select-option value=${l.id}><div slot="headline">${t(l.name)}</div></md-select-option>`)}
                              </md-filled-select>
                              <md-filled-text-field label=${t('createDialog.selectMeaning')} .value=${this.etymologyRootSearchFilter}
                                placeholder=${t('createDialog.etymologySearchPlaceholder')}
                                @input=${(e: Event) => { this.etymologyRootSearchFilter = (e.target as HTMLInputElement).value; this.onEtymologyRootSearchInput(); }}>
                              </md-filled-text-field>
                              <div class="search-mode-toggle" role="group" aria-label=${t('editDialog.searchMode')}>
                                <button
                                  type="button"
                                  class="search-mode-button ${this.etymologyRootSearchMode === 'includes' ? 'active' : ''}"
                                  @click=${() => this.setEtymologyRootSearchMode('includes')}
                                >
                                  ${t('editDialog.searchModeIncludes')}
                                </button>
                                <button
                                  type="button"
                                  class="search-mode-button ${this.etymologyRootSearchMode === 'equals' ? 'active' : ''}"
                                  @click=${() => this.setEtymologyRootSearchMode('equals')}
                                >
                                  ${t('editDialog.searchModeEquals')}
                                </button>
                              </div>
                              <md-outlined-button type="button" @click=${this.cancelAddEtymologyRoot}>${t('createDialog.cancel')}</md-outlined-button>
                              ${this.etymologyRootSearchLoading ? html`<div class="loading">${t('common.loading')}</div>` : ''}
                              ${!this.etymologyRootSearchLoading && this.etymologyRootSearchResults.length > 0 ? html`
                                <ul class="etymology-results">
                                  ${this.etymologyRootSearchResults
                                    .filter((item: any) => !etymology.roots.some(r => r.rootId === item.meaning.id))
                                    .map((item: any) => html`
                                    <li><button type="button" class="etymology-result-btn" @click=${() => this.handleAddEtymologyRoot(etymology.id, item)}>
                                      ${item.primarySpelling ?? ''} · ${t(item.meaning.type.name)} · ${t(item.meaning.language.name)} (${item.meaning.id})${item.displaySpelling ? ` — ${item.displaySpelling}` : ''}
                                    </button></li>
                                  `)}
                                </ul>
                              ` : ''}
                            </div>
                          ` : html`
                            <md-outlined-button type="button" @click=${() => this.startAddEtymologyRoot(etymology.id)}>${t('editDialog.addEtymologyRoot')}</md-outlined-button>
                          `}
                        </div>
                      ` : ''}
                    </div>
                  `)}
                  <md-outlined-button type="button" @click=${this.addEtymologyRow}>${t('editDialog.addEtymology')}</md-outlined-button>
                `}
              </section>
            </div>
          `}

          <div class="form-actions">
            <md-outlined-button type="button" @click=${this.handleCancel}>${t('createDialog.cancel')}</md-outlined-button>
          </div>
        `}
      </div>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'edit-entry-page': EditEntryPage
  }
}
